import { createClient } from '@/lib/supabase/client';

export type NotificationType = 'follow' | 'like' | 'comment' | 'reply' | 'save';

export interface Notification {
  id: string;
  recipient_id: string;
  actor_id: string | null;
  notification_type: NotificationType;
  post_id: string | null;
  comment_id: string | null;
  is_read: boolean;
  created_at: string;
  actor?: {
    id: string;
    display_name: string | null;
    username: string | null;
    avatar_url: string | null;
  };
}

export function getNotificationMessage(n: Notification): string {
  const actor = n.actor?.display_name || n.actor?.username || 'Someone';
  switch (n.notification_type) {
    case 'follow':
      return `${actor} started following you`;
    case 'like':
      return `${actor} liked your post`;
    case 'comment':
      return `${actor} commented on your post`;
    case 'reply':
      return `${actor} replied to your comment`;
    case 'save':
      return `${actor} saved your post`;
    default:
      return 'You have a new notification';
  }
}

async function triggerEmailNotification({
  recipientId,
  type,
  actorName,
  postTitle,
}: {
  recipientId: string;
  type: string;
  actorName?: string;
  postTitle?: string;
}) {
  try {
    const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
    const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
    if (!supabaseUrl || !supabaseAnonKey) return;

    await fetch(`${supabaseUrl}/functions/v1/send-email-notification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({ recipientId, type, actorName, postTitle }),
    });
  } catch {
    // Email is best-effort, never block the main action
  }
}

export async function createNotification({
  recipientId,
  actorId,
  type,
  postId,
  commentId,
  actorName,
  postTitle,
}: {
  recipientId: string;
  actorId: string;
  type: NotificationType;
  postId?: string;
  commentId?: string;
  actorName?: string;
  postTitle?: string;
}) {
  // Don't notify yourself
  if (recipientId === actorId) return;

  const supabase = createClient();
  await supabase.from('notifications').insert({
    recipient_id: recipientId,
    actor_id: actorId,
    notification_type: type,
    post_id: postId || null,
    comment_id: commentId || null,
    is_read: false,
  });

  // Trigger email notification (best-effort, non-blocking)
  triggerEmailNotification({ recipientId, type, actorName, postTitle });
}
